'use client';

import { useEffect, useRef, useState, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './AnimatedFooter.module.scss';

export interface AnimatedFooterProps {
  readonly className?: string;
}

interface FooterItem {
  id: string;
  type: 'social' | 'copyright';
  content: React.ReactNode;
}

// コンポーネント外に定義し再生成コストを排除
const FOOTER_ITEMS: readonly FooterItem[] = [
  {
    id: 'social',
    type: 'social',
    content: (
      <div className={styles.socialSection}>
        <div className={styles.socialLinks}>
          <a 
            href="https://twitter.com/baysgaia" 
            aria-label="Twitter" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.socialLink}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84"/>
            </svg>
          </a>
          <a 
            href="https://www.linkedin.com/company/baysgaia" 
            aria-label="LinkedIn" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.socialLink}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M18.52 0H1.477C.66 0 0 .646 0 1.442v17.114C0 19.352.66 20 1.477 20h17.043c.818 0 1.48-.648 1.48-1.443V1.442C20 .646 19.338 0 18.52 0zM5.932 17.043H2.96V7.495h2.972v9.548zM4.446 6.195a1.72 1.72 0 110-3.44 1.72 1.72 0 010 3.44zm12.597 10.848h-2.97v-4.64c0-1.108-.02-2.531-1.544-2.531-1.544 0-1.78 1.204-1.80 2.449v4.722H7.78V7.495h2.85v1.305h.04c.396-.75 1.364-1.543 2.807-1.543 3.004 0 3.557 1.977 3.557 4.547v5.239z"/>
            </svg>
          </a>
          <a 
            href="https://github.com/baysgaia" 
            aria-label="GitHub" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.socialLink}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" clipRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"/>
            </svg>
          </a>
        </div>
      </div>
    ),
  },
  {
    id: 'copyright',
    type: 'copyright',
    content: (
      <div className={styles.copyrightSection}>
        <p>&copy; 2025 BAYSGAiA Inc.</p>
      </div>
    ),
  }
];

const AnimatedFooterComponent = ({ className = '' }: AnimatedFooterProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // スライド切替ロジックを useCallback で安定化
  const startCycle = useCallback(() => {
    intervalRef.current = setInterval(() => {
      setIsVisible(false);

      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % FOOTER_ITEMS.length);
        setIsVisible(true);
      }, 300);
    }, 3000);
  }, []);

  useEffect(() => {
    startCycle();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startCycle]);

  const currentItem = FOOTER_ITEMS[currentIndex];

  if (!currentItem) {
    return null;
  }

  return (
    <div className={`${styles.animatedFooter} ${className}`}>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentItem.id}
          className={styles.footerContent}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ 
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }}
        >
          {currentItem.content}
        </motion.div>
      </AnimatePresence>
      
      {/* Progress indicators */}
      <div className={styles.indicators}>
        {FOOTER_ITEMS.map((_, index) => (
          <button
            key={index}
            className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
            onClick={() => {
              setCurrentIndex(index);
              setIsVisible(true);
              // Reset timer
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
              }
              setTimeout(startCycle, 3000);
            }}
            aria-label={`フッター項目 ${index + 1} を表示`}
          />
        ))}
      </div>
    </div>
  );
};

export const AnimatedFooter = memo(AnimatedFooterComponent);

export default AnimatedFooter;